/* eslint-disable jira/filename-case */
import cloneDeep from 'lodash/cloneDeep';
import {
	type ReportedTiming,
	type ReportedTimings,
	type SSRConfig,
	type SSRFeatureFlags,
	type SsrSuccessBreakdown,
	getEdgeTimingsIncludingCloudfront,
} from '@atlaskit/react-ufo/ssr';

import { getMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import performance from '@atlassian/jira-common-performance/src/performance.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import { SPA_SSR_RENDERED } from '@atlassian/jira-providers-spa-apdex-analytics/src/constants.tsx';
import { computeSsrSuccess } from '@jira/ssr__ssr-success/src/computeSsrSuccess.tsx';
import { failedResourcesCap } from '@jira/ssr__ssr-success/src/failedResourcesCap.tsx';

type TimingsOverrides = { startTimeOffsetMs?: number; prefix?: string };

/** given a timings object, add a timer that starts at the earliest start time and ends at the latest end time */
function insertTotalTiming(
	/**
	 * key of the total timing that will be inserted to the given timings object
	 */
	key: string,
	timings: Record<string, { duration: number; startTime: number }> | null,
) {
	const timingValues = timings && Object.values(timings);
	if (!timingValues || !timingValues.length) {
		return timings;
	}
	// oxlint-disable-next-line no-shadow
	const startTime = Math.min(...timingValues.map(({ startTime }) => startTime));
	const endTime = timingValues.reduce(
		// oxlint-disable-next-line no-shadow
		(max, { startTime, duration }) => Math.max(max, startTime + duration),
		0,
	);
	const duration = endTime - startTime;
	// oxlint-disable-next-line no-param-reassign
	timings[key] = {
		startTime,
		duration,
	};
	return timings;
}

/**
 * Apply key and startTimOffset overrides to a timings object
 */
function applyTimingOverrides(
	timings: ReportedTimings | null,
	{ startTimeOffsetMs = 0, prefix = '' }: TimingsOverrides = {},
): ReportedTimings | null {
	return (
		timings &&
		Object.fromEntries(
			Object.entries(timings).map(([key, value]) => {
				const startTime = value.startTime + startTimeOffsetMs;
				return [[prefix, key].filter(Boolean).join('/'), { ...value, startTime }];
			}),
		)
	);
}
export type SpaStateType = {
	'ssr/detail_request_trace'?: {
		data?: Record<string, ReportedTiming & { failureReason?: string }>;
	};
	'ssr/detail_render_trace'?: {
		data?: { traces?: Record<string, ReportedTiming> };
	};
	'ssr/relay_fetch'?: Record<string, ReportedTiming>;
	'ssr/metrics'?: {
		data?: {
			ssrTotal: number;
			ssrGetStyle: number;
			ssrPrefetch: number;
		};
	};
	'ssr/entry_point_status'?: string;
	'ssr/render_errors'?: {
		errors: Error[];
		count: number;
	};
	'ssr/circuit_breaker_open'?: boolean;
	'ssr/fetch_aborted'?: boolean;
	'ssr/failed_resources'?: string[];
	'ssr/failed_resource_count'?: number;
	'ssr/render_status'?: 'success' | 'failed';
};

export interface GlobalThisExtended {
	SPA_STATE?: SpaStateType;
	_JFGTimings?: { name: string; startTime: number; duration: number }[];
	_monolithTimings?: Record<string, { start: number; duration: number }>;
	document?: typeof document;
}
// oxlint-disable-next-line no-shadow-restricted-names
declare const globalThis: GlobalThisExtended;

function getTesseractTimings(): ReportedTimings | null {
	if (!globalThis.SPA_STATE) {
		return null;
	}

	const detailRenderTrace = globalThis.SPA_STATE['ssr/detail_render_trace'];

	const detailRelayFetch = globalThis.SPA_STATE['ssr/relay_fetch'];

	const detailRequestTrace = globalThis.SPA_STATE['ssr/detail_request_trace'];

	const highLevelMetrics = globalThis.SPA_STATE['ssr/metrics'];

	const timings = cloneDeep(detailRenderTrace?.data?.traces) || {};
	if (highLevelMetrics?.data) {
		if ('ssrTotal' in highLevelMetrics.data) {
			// empty string to represent the total time for these timings, applyTimingOverrides will add the relevant namespace later
			const totalKey = '';
			timings[totalKey] = { startTime: 0, duration: highLevelMetrics.data.ssrTotal };
		}
		if ('ssrGetStyle' in highLevelMetrics.data) {
			timings.styles = {
				startTime: highLevelMetrics.data.ssrGetStyle - highLevelMetrics.data.ssrGetStyle,
				duration: highLevelMetrics.data.ssrGetStyle,
			};
		}
		if ('ssrPrefetch' in highLevelMetrics.data) {
			const prefetchStartTime = Object.values(detailRequestTrace?.data || {}).reduce<number>(
				(min, { startTime }) => (min === -1 || min > startTime ? startTime : min),
				-1,
			);

			timings.prefetch = {
				startTime: prefetchStartTime,
				duration: highLevelMetrics.data.ssrPrefetch,
			};
		}
	}
	if (detailRequestTrace?.data) {
		Object.entries(detailRequestTrace.data).forEach(([key, value]: [string, ReportedTiming]) => {
			timings[`prefetch/${key}`] = value;
		});
	}
	if (detailRelayFetch) {
		const relayFetchTimings = Object.fromEntries(
			Object.entries(detailRelayFetch ?? {}).map(([key, value]: [string, ReportedTiming]) => [
				`relay/${key}`,
				value,
			]),
		);
		insertTotalTiming('relay', relayFetchTimings);
		Object.assign(timings, relayFetchTimings);
	}
	let timingsWithContext: ReportedTimings | null = timings;

	// If there's a server-timing for tesseract, that represents the pre-work before script execution starts, referred to as contextGlobalsSetup
	const serverTimingsByName = getServerTimingsByName();
	// This will become available when EDGE-8510 is complete
	const contextGlobalsSetup = serverTimingsByName.tesseract?.duration;
	if (contextGlobalsSetup != null) {
		timingsWithContext = applyTimingOverrides(timings, {
			startTimeOffsetMs: contextGlobalsSetup,
		});
		timingsWithContext &&
			Object.assign(timingsWithContext, {
				contextGlobalsSetup: { startTime: 0, duration: contextGlobalsSetup },
			});
	}

	return insertTotalTiming('', timingsWithContext); // while there is already a 'ssrTotal', it seems to exclude some timings, so we'll take the end to end as the actual total time
}

// add early-flush/ prefix to timing keys which are early flush timings
const jiraFeGatewayEarlyFlushKeys = new Set([
	'metatags',
	'feature-flags',
	'statsig',
	'inline-assets',
	'async-inline-assets',
	'css-assets',
	'priority-assets',
	'early-route-flush',
]);
// and filter out these keys since they are not UFO visualisation friendly
const jiraFeGatewayFilteredKeys = ['early-common-flush', 'prerender-total', 'resolve-assets'];
function getJiraFeGatewayTimings(): ReportedTimings | null {
	const rawTimings = globalThis._JFGTimings;
	if (!rawTimings) {
		return null;
	}
	const jiraFeGatewayTimings = Object.fromEntries(
		rawTimings
			.filter(({ name }) => !jiraFeGatewayFilteredKeys.includes(name))
			.map(({ startTime, duration, name }) => [
				jiraFeGatewayEarlyFlushKeys.has(name) ? `early-flush/${name}` : name,
				{ startTime, duration },
			]),
	);
	return insertTotalTiming(
		// there's no end to end timer so we calculate one
		'',
		jiraFeGatewayTimings,
	);
}

function getLegacyMonolithTimingDuration(timingMetricKey: string) {
	/* oxlint-disable-next-line typescript/consistent-type-assertions */
	const inputElementWithTimingValue = globalThis.document?.querySelector(
		`#jira_request_timing_info [title="${timingMetricKey}"]`,
	) as HTMLInputElement;
	return Number(inputElementWithTimingValue?.value ?? 0);
}

function getLegacyJiraMonolithTimings(): ReportedTimings | null {
	const duration = getLegacyMonolithTimingDuration('jira.request.server.time');
	if (!duration) {
		// if we don't have this, fallback to no jira timings, we might be going through bifrost instead
		return null;
	}
	const timings = {
		// empty string to represent the total time for these timings, applyTimingOverrides will add the relevant namespace later
		'': {
			startTime: 0,
			duration,
		},
		'flush-head': {
			startTime: 0,
			duration: getLegacyMonolithTimingDuration('jira.request.server.head.time'),
		},
	};

	return timings;
}

const monolithTimerRenames: Record<string, string> = {
	'jira-request-server-time': '', // empty string to represent the total time for these timings, applyTimingOverrides will add the relevant namespace later
};

function getJiraMonolithTimings(): ReportedTimings | null {
	if (!globalThis._monolithTimings) {
		return getLegacyJiraMonolithTimings();
	}
	return Object.fromEntries(
		Object.entries(globalThis._monolithTimings).map(([name, { start: startTime, duration }]) => [
			monolithTimerRenames[name] ?? name,
			{ startTime, duration },
		]),
	);
}

function getBifrostGatewayTimings(
	jiraBeTimings: ReportedTimings | null,
	tesseractTimings: ReportedTimings | null,
): ReportedTimings | null {
	if (jiraBeTimings || !tesseractTimings) {
		return null;
	}

	const serverTimingsByName = getServerTimingsByName();
	// This will become available when EDGE-8510 is complete
	const bifrostInternal = serverTimingsByName.bifrost;
	const bifrostInternalDuration = bifrostInternal?.duration ?? 0;

	const timings = {
		...(bifrostInternal && {
			internal: {
				startTime: 0,
				duration: bifrostInternalDuration,
			},
		}),
		tesseract: {
			startTime: bifrostInternalDuration,
			duration: tesseractTimings[''].duration,
		},
	};
	return insertTotalTiming('', timings);
}

/**
 * Get the offset required to align the ending of the timerToShift with the ending of the referenceTimer
 * But if the referenceTimer is smaller than the timerToShift (it shouldn't be, this is for timerToShifts that in practice are triggered within the reference timer)
 * then at least align the start times
 */
function getOffsetToAlignTimers(
	referenceTimer: ReportedTiming | undefined | null,
	timerToShift: ReportedTiming | undefined | null,
): number | undefined | null {
	if (!referenceTimer || !timerToShift) {
		return null;
	}
	if (referenceTimer.duration >= timerToShift.duration) {
		return (
			referenceTimer.startTime +
			referenceTimer.duration -
			(timerToShift.startTime + timerToShift.duration)
		);
	}
	return referenceTimer.startTime - timerToShift.startTime;
}
function getJfgOffset({
	jiraFeGatewayTimings,
	jiraBeTimings,
}: {
	jiraFeGatewayTimings: Record<string, ReportedTiming> | null;
	jiraBeTimings: Record<string, ReportedTiming> | null;
}) {
	// Push back the start of jfg so it's ending aligns with either the full monolith time or if we have the more specific timer for fetching jfg, use that
	const jiraBeTiming =
		jiraBeTimings?.['jira-monolith/feGateway'] ?? jiraBeTimings?.['jira-monolith'];
	return getOffsetToAlignTimers(jiraBeTiming, jiraFeGatewayTimings?.['']) ?? 0;
}

/**
 * Get the offset required to align the ending of the tesseract timer with either
 * the ending of the jira-fe-gateway/fetch-tesseract timer or the bifrost-gateway timer
 */
function getTesseractOffset({
	tesseractTimings,
	jiraFeGatewayTimings,
	bifrostGatewayTimings,
}: {
	tesseractTimings: Record<string, ReportedTiming> | null;
	jiraFeGatewayTimings: Record<string, ReportedTiming> | null;
	bifrostGatewayTimings: Record<string, ReportedTiming> | null;
}) {
	const jiraFeGatewayTiming = jiraFeGatewayTimings?.['jira-fe-gateway/fetch-tesseract'];
	const jiraBeTesseractOffest = getOffsetToAlignTimers(jiraFeGatewayTiming, tesseractTimings?.['']);

	const bifrostGatewayTiming = bifrostGatewayTimings?.['bifrost-gateway'];
	const bifrostOffset = getOffsetToAlignTimers(bifrostGatewayTiming, tesseractTimings?.['']);

	return bifrostOffset ?? jiraBeTesseractOffest ?? 0;
}

function getPerformanceNavigationTiming() {
	// getEntriesByType doesn't change the returned type based on the given type key
	// oxlint-disable-next-line typescript/consistent-type-assertions
	return performance.getEntriesByType('navigation')?.[0] as PerformanceNavigationTiming;
}

function getServerTimingsByName() {
	const serverTimingsByName: Record<string, PerformanceServerTiming> = Object.fromEntries(
		getPerformanceNavigationTiming()?.serverTiming?.map(
			(timing) => [timing.name, timing] as const,
		) ?? [],
	);
	return serverTimingsByName;
}

export function getTimings(): ReportedTimings | null {
	const jiraBeTimings = getJiraMonolithTimings();
	const jiraFeGatewayTimings = getJiraFeGatewayTimings();
	const tesseractTimings = getTesseractTimings();
	const bifrostGatewayTimings = getBifrostGatewayTimings(jiraBeTimings, tesseractTimings);

	const edgeTimings = getEdgeTimingsIncludingCloudfront();

	const edgeTime =
		(edgeTimings?.['edge/atl-edge/internal']?.duration ?? 0) +
		(edgeTimings?.['edge/atl-edge/internal']?.startTime ?? 0);
	const finalJiraBeTimings = applyTimingOverrides(jiraBeTimings, {
		startTimeOffsetMs: edgeTime,
		prefix: 'jira-monolith',
	});
	const finalBifrostGatewayTimings = applyTimingOverrides(bifrostGatewayTimings, {
		startTimeOffsetMs: edgeTime,
		prefix: 'bifrost-gateway',
	});
	const jfgOffset = getJfgOffset({ jiraFeGatewayTimings, jiraBeTimings: finalJiraBeTimings });
	const finalJfgTimings = applyTimingOverrides(jiraFeGatewayTimings, {
		startTimeOffsetMs: jfgOffset,
		prefix: 'jira-fe-gateway',
	});
	const tesseractOffset = getTesseractOffset({
		tesseractTimings,
		jiraFeGatewayTimings: finalJfgTimings,
		bifrostGatewayTimings: finalBifrostGatewayTimings,
	});
	const finalTesseractTimings = applyTimingOverrides(tesseractTimings, {
		startTimeOffsetMs: tesseractOffset,
		prefix: 'tesseract',
	});
	const timings = {
		...finalJiraBeTimings,
		...finalJfgTimings,
		...finalBifrostGatewayTimings,
		...finalTesseractTimings,
	};
	insertTotalTiming('total', timings); // UFO treats 'total' as a special key for ssr timings that translates to 'ssr'
	return Object.keys(timings).length ? timings : null;
}

const SSR_FF_META_TAG = 'spa-service-flags';
const EARLY_FLUSH_END_MARK = 'ssr.spa.early-common-flush:end';

let ffCache: SSRFeatureFlags | null;
let ffCacheInitialiser: Function | null = () => {
	try {
		const metaTagValue = getMeta(SSR_FF_META_TAG);
		if (metaTagValue === null) return null;
		ffCache = JSON.parse(metaTagValue) || null;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		ffCache = null;
	}
	ffCacheInitialiser = null;
};

function getFeatureFlags(): SSRFeatureFlags | null {
	if (ffCacheInitialiser != null) ffCacheInitialiser();
	return ffCache;
}

function isAnyRouteResourceFetchErrors() {
	return Object.values(globalThis.SPA_STATE?.['ssr/detail_request_trace']?.data ?? {}).some(
		(timing) => timing?.failureReason,
	);
}

// Names of the route resources that recorded a `failureReason`. Keyed by a
// bounded, closed set of resource-type names (no PII).
function getFailedRouteResources(): string[] {
	return Object.entries(globalThis.SPA_STATE?.['ssr/detail_request_trace']?.data ?? {})
		.filter(([, timing]) => timing?.failureReason)
		.map(([resourceName]) => resourceName);
}

function isAnyReactRenderErrors() {
	return !!globalThis.SPA_STATE?.['ssr/render_errors']?.count;
}

function isAnyEntryPointFetchErrors() {
	return !!globalThis.SPA_STATE?.['ssr/entry_point_status']?.match(/error|timeout/);
}

function isEarlyFlushSuccessful() {
	return !!performance?.getEntriesByName(EARLY_FLUSH_END_MARK);
}

function getDoneMark() {
	return getMark(SPA_SSR_RENDERED)?.startTime || null;
}

const isCircuitBreakerOpen = (): boolean =>
	globalThis.SPA_STATE?.['ssr/circuit_breaker_open'] === true;

const wasFetchAborted = (): boolean => globalThis.SPA_STATE?.['ssr/fetch_aborted'] === true;

/**
 * The renderer's services/html commits 'failed' when hasFailedSSR() reports a
 * top-level Suspense fallback was rendered for the page (i.e. the renderer
 * finished but emitted placeholder content for resources that didn't return).
 */
const wasSuspenseFallbackEmitted = (): boolean =>
	globalThis.SPA_STATE?.['ssr/render_status'] === 'failed';

/**
 * The static fallback bypasses the renderer entirely, so SPA_STATE is empty.
 * The renderer always commits `ssr/render_status` — checking its absence is
 * fail-safe against any future fallback path that forgets to advertise itself.
 */
const wasStaticFallbackServed = (): boolean =>
	!globalThis.SPA_STATE || globalThis.SPA_STATE['ssr/render_status'] === undefined;

const getFailedResources = (): string[] => globalThis.SPA_STATE?.['ssr/failed_resources'] ?? [];

const getFailedResourceCount = (): number =>
	globalThis.SPA_STATE?.['ssr/failed_resource_count'] ?? 0;

/**
 * Client-side breakdown built from `globalThis.SPA_STATE` and `performance`
 * entries. Both `getSsrSuccessBreakdown` (UFO breakdown payload) and
 * `getSsrPhaseSuccess().done` (UFO `'ssr:success'` boolean) derive from this so
 * they cannot drift. The success verdict itself is computed by the shared,
 * type-driven `computeSsrSuccess` (`@jira/ssr__ssr-success`), which the SSR
 * renderer also uses, so server and client agree on the formula by construction.
 * The `SsrSuccessBreakdown` type is owned by `@atlaskit/react-ufo/ssr`.
 */
const buildBreakdown = (): SsrSuccessBreakdown => {
	const failedResourceCount = getFailedResourceCount();
	const failedRouteResources = getFailedRouteResources();
	return {
		servedStaticFallback: wasStaticFallbackServed(),
		// The SPA never reached the SPA_SSR_RENDERED mark — a failure the other
		// reason flags cannot otherwise attribute.
		notRendered: !getDoneMark(),
		hadRuntimeError: isAnyReactRenderErrors(),
		circuitBreakerOpen: isCircuitBreakerOpen(),
		hadFailedResources: failedResourceCount > 0,
		fetchAborted: wasFetchAborted(),
		emittedSuspenseFallback: wasSuspenseFallbackEmitted(),
		// Note: deliberately does NOT call isEarlyFlushSuccessful(), which has a
		// historical bug (returns truthy for an empty array). Fixing the helper
		// would change the legacy gate-off `done` branch, which is out of scope
		// for this PR; read the performance entry directly here.
		hadEarlyFlushFailure: (performance?.getEntriesByName(EARLY_FLUSH_END_MARK)?.length ?? 0) === 0,
		hadRouteResourceFailure: isAnyRouteResourceFetchErrors(),
		failedResources: getFailedResources(),
		failedResourceCount,
		failedRouteResources: failedRouteResources.slice(0, failedResourcesCap),
		failedRouteResourceCount: failedRouteResources.length,
	};
};

// SPA_STATE is inlined once into the HTML and never mutated after load, so the
// breakdown is constant per page. Memoize keyed on SPA_STATE identity so the two
// UFO callers (getSsrSuccessBreakdown, getSsrPhaseSuccess) share one traversal.
let cachedBreakdownKey: unknown;
let cachedBreakdownValue: SsrSuccessBreakdown | undefined;
const getCachedBreakdown = (): SsrSuccessBreakdown => {
	const key = globalThis.SPA_STATE;
	if (cachedBreakdownValue === undefined || cachedBreakdownKey !== key) {
		cachedBreakdownKey = key;
		cachedBreakdownValue = buildBreakdown();
	}
	return cachedBreakdownValue;
};

const getSsrSuccessBreakdown = (): SsrSuccessBreakdown | undefined => {
	if (!fg('jfp-magma-ssr-success-breakdown')) return undefined;
	return getCachedBreakdown();
};

export const jiraSsrConfig: SSRConfig = {
	getDoneMark,
	getSsrPhaseSuccess: () => {
		const prefetch =
			!!globalThis.SPA_STATE && !isAnyEntryPointFetchErrors() && !isAnyRouteResourceFetchErrors();
		const earlyFlush = isEarlyFlushSuccessful();
		const render = !isAnyReactRenderErrors();

		// New definition (both gates on) vs legacy (either gate off). The new
		// branch needs no separate done-mark check: a missing SPA_SSR_RENDERED
		// mark is the `notRendered` reason in the breakdown, so computeSsrSuccess
		// already returns false for it. The legacy branch keeps getDoneMark()
		// because it does not read the breakdown.
		// The gates can't be preconditioned in Statsig: the new definition only
		// works once the breakdown is emitted (gate-on), so both must be read
		// together right here.
		const done =
			// eslint-disable-next-line jira/ff/no-preconditioning -- see comment above
			fg('jfp-magma-ssr-success-breakdown') && fg('jfp-magma-redefine-ssr-success')
				? computeSsrSuccess(getCachedBreakdown())
				: !!getDoneMark() && render && earlyFlush && prefetch;

		return {
			prefetch,
			earlyFlush,
			done,
		};
	},
	getTimings,
	getFeatureFlags,
	getSsrSuccessBreakdown,
} as const;
